import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Factory, HardHat, Flame, Truck, Stethoscope, GraduationCap, Building2, Bolt, Briefcase, ShieldAlert, Warehouse, Users } from "lucide-react";
import axios from "axios";
import API from "@/lib/api";

const ICONS = { Factory, HardHat, Flame, Truck, Stethoscope, GraduationCap, Building2, Bolt, Briefcase, ShieldAlert, Warehouse, Users };

export default function Industries() {
  const [industries, setIndustries] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/industries`)
      .then(({ data }) => setIndustries(Array.isArray(data) ? data : []))
      .catch(console.error);
  }, []);

  return (
    <div data-testid="industries-page">
      {/* HERO */}
      <section className="relative bg-white pt-6 md:pt-10 pb-12 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="glow-blob bg-[#00A8E8]/20 w-[500px] h-[500px] -top-32 -left-20 pulse-glow" />
        <div className="container-ngxp relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span className="overline">Industries</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-[#0A0B10]">
                Built for Organizations Managing <span className="gradient-text-glow">Complex Operations</span>
              </h1>
              <p className="mt-6 text-lg text-gray-600 leading-relaxed max-w-2xl">
                NGXP solutions can be adapted for industries and organizations that need real-time visibility, safety, automation, AI intelligence, simulation, digital twins, and operational decision support.
              </p>
              <div className="mt-8">
                <Link to="/contact" className="btn-primary-gradient" data-testid="industries-cta">
                  Discuss Your Industry <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative rounded-3xl overflow-hidden border border-gray-100 shadow-2xl blob-float">
                <img src="/banner.jpg" alt="Industries" className="w-full h-[400px] object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDUSTRIES GRID */}
      <section className="bg-[#F8F9FA] py-14 md:py-18">
        <div className="container-ngxp">
          <div className="max-w-3xl reveal">
            <span className="overline">Where We Apply</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-[#0A0B10]">
              Adaptable across <span className="gradient-text-glow">complex, high-value industries.</span>
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((ind, i) => {
              const Icon = ICONS[ind.icon] || Factory;
              return (
                <div key={i} className="ngxp-card group reveal" data-testid={`industry-${i}`}>
                  <div className="flex items-start justify-between">
                    <div className="icon-badge"><Icon className="w-6 h-6" /></div>
                  </div>
                  <h3 className="font-display text-lg font-semibold">{ind.name}</h3>
                  <p className="mt-3 text-sm text-gray-600 leading-relaxed">{ind.desc}</p>
                  <div className="mt-5 pt-4 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs uppercase tracking-[0.18em] font-bold gradient-text-glow">Adaptable Solution</span>
                    <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-[#7B2CBF] transition-colors" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* DARK NOTE */}
      <section className="bg-[#0A0B10] text-white py-12 md:py-16 relative overflow-hidden">
        <div className="absolute inset-0 dark-grid-pattern opacity-40" />
        <div className="container-ngxp relative z-10 text-center max-w-3xl mx-auto">
          <p className="font-display text-2xl md:text-3xl leading-snug">
            We deliberately use the phrase <span className="gradient-text-glow">"NGXP solutions can be adapted for…"</span> — because every operation is different. Our architecture is industry-aware, but solution-neutral.
          </p>
          <Link to="/contact" data-testid="industries-final-cta" className="mt-10 inline-flex items-center gap-2 btn-primary-gradient">
            Map NGXP to Your Operation <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
